/**
 * Deduplicates concurrent loads per key and lets only the most recent key
 * store its result. The permissions provider keys loads by user, session and
 * tenant: a load for a new tenant must start even while the previous tenant's
 * is still in flight, and the older answer must be discarded when it lands.
 */
export interface LatestRequestGuard {
  /** Marks `key` as the latest request. False when a load for `key` is already running. */
  begin(key: string): boolean;
  /** Whether `key` is still the latest request, i.e. its result may be stored. */
  isLatest(key: string): boolean;
  /** Marks the load for `key` as finished. */
  end(key: string): void;
}

export function createLatestRequestGuard(): LatestRequestGuard {
  const inFlight = new Set<string>();
  let latest: string | null = null;
  return {
    begin(key) {
      latest = key;
      if (inFlight.has(key)) return false;
      inFlight.add(key);
      return true;
    },
    isLatest(key) {
      return latest === key;
    },
    end(key) {
      inFlight.delete(key);
    },
  };
}
